import React, { useState, useEffect, useMemo, useImperativeHandle, forwardRef } from 'react';
import { DataModelObject } from '../types/dataModel';
import { DataModelApiService } from '../services/dataModelApi';
import { Button, Text, Icon } from './index';
import { IconName } from './Icon/Icon.type';
import { IfHasPermission } from './IfHasPermission';
import { Permissions } from '../constants';

interface DataModelPreviewProps {
  projectId: string;
  dataModel: DataModelObject[];
  onDataModelChange: (updatedDataModel: DataModelObject[]) => void;
  isLoading?: boolean;
  project?: { permissions?: string[] } | null;
  isEditing?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
  isTapLinked?: boolean;
}

export interface DataModelPreviewRef {
  handleSave: () => Promise<void>;
  handleCancel: () => void;
  hasError: () => boolean;
}

const EMPTY_DATA_MODEL_TEMPLATE = '[]';

const DataModelPreview = forwardRef<DataModelPreviewRef, DataModelPreviewProps>(({
  projectId,
  dataModel,
  onDataModelChange,
  isLoading = false,
  project,
  isEditing: externalIsEditing,
  onEditingChange,
  isTapLinked = false,
}, ref) => {

  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = onEditingChange || setInternalIsEditing;
  const [editableJson, setEditableJson] = useState('');
  const [jsonError, setJsonError] = useState('');

  const hasUpdatePermission = useMemo(() => project?.permissions?.includes(Permissions.UpdateProjectPermission) || false, [project]);
  const canEdit = useMemo(() => hasUpdatePermission && !isTapLinked, [hasUpdatePermission, isTapLinked]);

  // Debug logging
  useEffect(() => {
    console.log('DataModelPreview - isLoading:', isLoading, 'dataModel length:', dataModel?.length || 0);
  }, [isLoading, dataModel]);

  useEffect(() => {
    const isEmpty = !dataModel || !Array.isArray(dataModel) || dataModel.length === 0;
    setEditableJson(isEmpty ? EMPTY_DATA_MODEL_TEMPLATE : JSON.stringify(dataModel, null, 2));
  }, [dataModel]);

  const handleEdit = () => {
    setIsEditing(true);
    setJsonError('');
  };

  const handleSave = async () => {
    try {
      const parsed = JSON.parse(editableJson);

      // Basic validation
      if (!Array.isArray(parsed)) {
        throw new Error('Data model must be an array');
      }

      // Validate each object has required fields
      for (const obj of parsed) {
        if (!obj.object_name || !obj.object_type || !obj.elements) {
          throw new Error('Invalid data model object structure');
        }
      }

      // Save to backend
      const result = await DataModelApiService.saveDataModel(projectId, parsed);

      if (!result.success) {
        throw new Error(result.error || 'Failed to save data model');
      }

      // Update local state
      onDataModelChange(parsed);
      setIsEditing(false);
      setJsonError('');

      console.log('Data model saved successfully to backend');
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  };

  const handleCancel = () => {
    const cancelEmpty = !dataModel || !Array.isArray(dataModel) || dataModel.length === 0;
    setEditableJson(cancelEmpty ? EMPTY_DATA_MODEL_TEMPLATE : JSON.stringify(dataModel, null, 2));
    setIsEditing(false);
    setJsonError('');
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    handleSave,
    handleCancel,
    hasError: () => !!jsonError,
  }));

  const renderDataModelVisual = () => {
    if (!dataModel || !Array.isArray(dataModel) || dataModel.length === 0) {
      return (
        <div className="text-center py-bg-v2-3xl">
          <Text variant="text-sm" weight="medium" color="tertiary" align="center">
            No Data Model Available
          </Text>
          <Text variant="text-xs" color="tertiary" align="center" className="mt-bg-v2-xs">
            Switch to JSON view to edit or start chatting to generate a data model
          </Text>
        </div>
      );
    }

    const parentObjects = dataModel.filter(obj => !obj.parent_object_name);
    const childrenByParent = dataModel
      .filter(obj => !!obj.parent_object_name)
      .reduce<Record<string, typeof dataModel>>((acc, obj) => {
        const parent = obj.parent_object_name!;
        if (!acc[parent]) acc[parent] = [];
        acc[parent].push(obj);
        return acc;
      }, {});

    const parentLabelMap = dataModel.reduce<Record<string, string>>((acc, obj) => {
      acc[obj.object_name] = obj.object_label;
      return acc;
    }, {});

    const renderObjectCard = (obj: typeof dataModel[number], isChild = false) => (
      <div
        key={obj.object_name}
        className={`border rounded-bg-v2-lg p-bg-v2-xl bg-bg-primary-alt ${isChild ? 'border-utility-purple-200 ml-bg-v2-xl' : 'border-border-primary'}`}
      >
        <div className="flex justify-between items-start mb-bg-v2-lg">
          <div>
            <Text variant="text-md" weight="semibold" color="primary">{obj.object_label}</Text>
            <Text variant="text-sm" color="secondary">{obj.object_name}</Text>
            <div className="flex flex-wrap items-center gap-bg-v2-xs mt-bg-v2-xs">
              <span className={`inline-block px-bg-v2-xs py-bg-v2-xxs text-bg-v2-xs rounded-bg-v2-full ${obj.object_type === 'Reference'
                ? 'bg-utility-blue-100 text-utility-blue-800'
                : 'bg-utility-green-100 text-utility-green-800'
                }`}>
                {obj.object_type}
              </span>
            </div>
            {obj.parent_object_name && (
              <span className="inline-flex items-center gap-bg-v2-xxs px-bg-v2-xs py-bg-v2-xxs text-bg-v2-xs rounded-bg-v2-full mt-bg-v2-xs bg-utility-purple-100 text-utility-purple-800">
                <Icon name={IconName.ProjectTree} size="xs" inheritColor />
                Parent: {parentLabelMap[obj.parent_object_name] || obj.parent_object_name}
              </span>
            )}
          </div>
        </div>

        {obj.description && (
          <Text variant="text-sm" color="secondary" className="mb-bg-v2-sm">{obj.description}</Text>
        )}

        {obj.elements && obj.elements.length > 0 && (
          <div className="mt-bg-v2-lg">
            <Text variant="text-sm" weight="medium" color="primary" className="mb-bg-v2-xs">Elements:</Text>
            <div className="space-y-bg-v2-md">
              {obj.elements.map((element, elemIndex) => (
                <div key={elemIndex} className="p-bg-v2-md bg-bg-primary rounded-bg-v2-md border border-border-secondary">
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <span className="font-bg-v2-medium text-bg-v2-sm text-text-primary">{element.element_name}</span>
                      <span className="ml-bg-v2-xs text-text-tertiary text-bg-v2-xs">({element.data_type})</span>
                      {element.required && (
                        <span className="ml-bg-v2-xxs text-bg-v2-xs text-text-error-primary">*</span>
                      )}
                    </div>
                    <div className="flex space-x-bg-v2-xs text-bg-v2-xs">
                      {element.read_only && (
                        <span className="px-bg-v2-xs py-bg-v2-xxs bg-utility-gray-100 text-utility-gray-600 rounded-bg-v2-sm">RO</span>
                      )}
                      {element.system_generated && (
                        <span className="px-bg-v2-xs py-bg-v2-xxs bg-utility-purple-100 text-utility-purple-600 rounded-bg-v2-sm">SYS</span>
                      )}
                      {element.multi_valued && (
                        <span className="px-bg-v2-xs py-bg-v2-xxs bg-utility-orange-100 text-utility-orange-600 rounded-bg-v2-sm">MULTI</span>
                      )}
                    </div>
                  </div>
                  {Array.isArray(element.choices) && element.choices.length > 0 && element.choices.every((c) => typeof c === 'string') && (
                    <div className="mt-bg-v2-xs flex flex-wrap gap-bg-v2-xxs">
                      {element.choices.map((choice, ci) => (
                        <span key={ci} className="px-bg-v2-xs py-bg-v2-xxs text-bg-v2-xs bg-utility-blue-50 text-utility-blue-700 rounded-bg-v2-sm border border-utility-blue-200">
                          {choice}
                        </span>
                      ))}
                    </div>
                  )}
                  {element.reference_description && (
                    <Text variant="text-xs" color="tertiary" className="mt-bg-v2-xxs italic">
                      {element.reference_description}
                    </Text>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );

    return (
      <div className="space-y-bg-v2-md">
        {parentObjects.map((obj) => (
          <div key={obj.object_name} className="space-y-bg-v2-sm">
            {renderObjectCard(obj)}
            {childrenByParent[obj.object_name]?.map((child) => renderObjectCard(child, true))}
          </div>
        ))}
        {/* Render orphan children whose parent is not in the model */}
        {dataModel
          .filter(obj => obj.parent_object_name && !dataModel.some(p => p.object_name === obj.parent_object_name))
          .map((obj) => renderObjectCard(obj, true))}
      </div>
    );
  };

  // Show loading indicator when loading
  if (isLoading) {
    return (
      <div className="border-2 border-dashed border-border-primary rounded-bg-v2-lg h-full flex items-center justify-center">
        <div className="text-center">
          <Icon name={IconName.Loading} spin className="mx-auto mb-bg-v2-xs" size="xl" variant="brand-utility" />
          <Text variant="text-sm" color="secondary">Loading data model...</Text>
        </div>
      </div>
    );
  }

  const isEmpty = !dataModel || !Array.isArray(dataModel) || dataModel.length === 0;

  if (isEmpty && !isEditing) {
    return (
      <div className="border-2 border-dashed border-border-primary rounded-bg-v2-lg h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <Text variant="text-sm" weight="medium" color="secondary" align="center">No Data Model Generated Yet</Text>
          <Text variant="text-xs" color="tertiary" align="center" className="mt-bg-v2-xs">
            Start chatting with the Data Model Assistant to generate your data model, or switch to edit mode to add manually.
          </Text>
          <div className="mt-bg-v2-md p-bg-v2-sm bg-utility-blue-50 border border-utility-blue-200 rounded-bg-v2-lg text-center">
            <Text variant="text-xs" color="brand-primary" align="center">
              💡 Tip: Describe your application's data requirements to get started
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-h-full overflow-hidden">
      {/* Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {isEditing ? (
          <div className="flex flex-col h-full space-y-bg-v2-sm">
            <div className="bg-bg-primary-alt p-bg-v2-sm rounded-bg-v2-lg border border-border-secondary flex-shrink-0">
              <Text variant="text-sm" color="secondary" className="mb-bg-v2-xs">
                {
                  !hasUpdatePermission ?
                    'You are not authorized to edit the data model.'
                    : isTapLinked ?
                      'Data model cannot be edited when TAP site is linked. Unlink the TAP site to enable editing.'
                      : 'Edit the JSON data model below. Changes will be saved automatically when you click "Save Changes".'
                }
              </Text>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              <textarea
                disabled={!canEdit}
                value={editableJson}
                onChange={(e) => setEditableJson(e.target.value)}
                className={`w-full h-full p-bg-v2-xl font-mono text-bg-v2-sm bg-bg-primary text-text-primary placeholder:text-text-placeholder border rounded-bg-v2-lg focus:ring-2 focus:ring-utility-brand-500 focus:border-utility-brand-500 resize-none ${jsonError ? 'border-utility-error-500' : 'border-border-secondary'
                  }`}
                placeholder="Edit your data model JSON..."
                spellCheck={false}
              />
            </div>
            {jsonError && (
              <div className="p-bg-v2-sm bg-utility-error-50 border border-utility-error-200 rounded-bg-v2-lg flex-shrink-0">
                <Text variant="text-sm" color="error">{jsonError}</Text>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-1 min-h-0">
            {renderDataModelVisual()}
          </div>
        )}
      </div>
    </div>
  );
});

DataModelPreview.displayName = 'DataModelPreview';

export default DataModelPreview;
